import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";

import BasePadding from "@cayuse/shared-react.components.base-padding";
import Breadcrumbs from "@cayuse/shared-react.components.breadcrumbs";
import LoadingIndicator from "@cayuse/shared-react.components.loading-indicator";
import SectionHeader from "@cayuse/shared-react.components.section-header";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import { Ref } from "semantic-ui-react";
import { validate as uuidValidate } from "uuid";


import AwardSummary from "components/AwardSummary";
import RelatedRecords from "components/RelatedRecords";
import Tabs from "components/Tab/Tabs";
import AwardAdminOnlyView from "containers/Awards/Detail/AdminOnly";
import AwardAttachmentsView from "containers/Awards/Detail/Attachments";
import AwardFormView from "containers/Awards/Detail/Form";
import FundsSummaryView from "containers/Awards/Detail/Funds";
import AwardHistoryView from "containers/Awards/Detail/History";
import AwardNotesView from "containers/Awards/Detail/Notes";
import AwardsRouting from "containers/Awards/Detail/Routing";
import Subaward from "containers/Awards/Detail/Subaward";
import AwardTasksView from "containers/Awards/Detail/Tasks";
import {
	AWARDS_BASE_ROUTE,
	FORM_SUBMISSION_TYPES,
	PROJECTS_BASE_ROUTE,
	PROJECT_AWARD_ADMINISTRATOR,
	ROLE_SP_AWARD_ADMINISTRATOR,
	ROLE_SP_SYSTEM_ADMINISTRATOR,
} from "globals/constants";
import { useDictionary, useHasRoles } from "hooks";
import { resetAward } from "store/actions/award";
import { awardSelectors } from "store/selectors";
import { spUrl } from "utils";

import { resetForm } from "../../../store/actions/form";
import styles from "./styles.css";

const AwardDetailView = () => {
	const award = useSelector(awardSelectors.award);

	const [ justActivatedSectionLabel, setJustActivatedSectionLabel ] = useState("");
	const [ isFormComplete, setIsFormComplete ] = useState(false);
	const [ isUploading, setIsUploading ] = useState(false);
	const dictionary = useDictionary("awards.awardDetails");
	const tabsRef = useRef(null);
	const { awardId } = useParams();
	const history = useHistory();

	useEffect(() => {
		return () => {
			resetForm();
			resetAward();
		};
	}, []);

	useEffect(() => {
		resetAward();
		if (!uuidValidate(awardId)) {
			history.push("/sp/awards");
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ awardId ]);

	const breadcrumbs = (
		<Breadcrumbs breadcrumbs={[
			{
				key: 0,
				text: dictionary?.breadcrumbRoot || "My Active Awards",
				link: spUrl(AWARDS_BASE_ROUTE),
			},
			{
				key: 1,
				text: award?.projectTitle,
				link: spUrl(`${PROJECTS_BASE_ROUTE}/${award?.projectId}`),
			},
			{
				key: 2,
				text: award?.awardNumber,
			},
		]}
		/>
	);

	const {
		[PROJECT_AWARD_ADMINISTRATOR]: isAwardAdmin,
		[ROLE_SP_AWARD_ADMINISTRATOR]: isSpAwardAdmin,
		[ROLE_SP_SYSTEM_ADMINISTRATOR]: isSystemAdmin,
	} = useHasRoles([
		PROJECT_AWARD_ADMINISTRATOR,
		ROLE_SP_AWARD_ADMINISTRATOR,
		ROLE_SP_SYSTEM_ADMINISTRATOR,
	]);
	const showAdminOnlyTab = isSystemAdmin || isAwardAdmin || isSpAwardAdmin;

	const panes = [ // todo: remember which tab was open
		{
			menuItem: dictionary.tabLabels?.form || "Award Form",
			render: () => (
				<div>
					<AwardFormView
						isFormComplete={isFormComplete}
						setIsFormComplete={setIsFormComplete}
						handleFormLoaded={setIsFormComplete}
						justActivatedSectionLabel={justActivatedSectionLabel}
						setJustActivatedSectionLabel={setJustActivatedSectionLabel}
					/>
				</div>
			),
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.routing || "Routing",
			render: () => <AwardsRouting />,
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.history || "Award History",
			render: () => <AwardHistoryView />,
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.funds || "Funds",
			render: () => <FundsSummaryView />,
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.subawards || "Subawards",
			render: () => <Subaward />,
			ref: useRef(null),
		},
		/*{ // todo: uncomment when tab is scoped for development
			menuItem: dictionary.tabLabels?.negotiations || "Negotiations",
			render: () => Negotiations Tab,
			ref: useRef(null),
		},*/
		{
			menuItem: dictionary.tabLabels?.tasks || "Tasks",
			render: () => <AwardTasksView/>,
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.notes || "Notes",
			render: () => <AwardNotesView/>,
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.attachments || "Attachments",
			render: () => (
				<AwardAttachmentsView
					tabsRef={tabsRef}
					formTabText={dictionary.tabLabels?.form}
					setJustActivatedSectionLabel={setJustActivatedSectionLabel}
					setIsUploading={setIsUploading}
					isUploading={isUploading}
				/>
			),
			ref: useRef(null),
		},
		{
			menuItem: dictionary.tabLabels?.relatedRecords || "Related Records",
			render: () => <RelatedRecords formType={FORM_SUBMISSION_TYPES.AWARD} />,
			ref: useRef(null),
		}
	];

	const adminOnly = {
		menuItem: dictionary.tabLabels?.adminOnly || "Admin Only",
		render: () => <AwardAdminOnlyView setIsUploading={setIsUploading}/>,
		ref: useRef(null),
	};

	if (showAdminOnlyTab) {
		panes.push(adminOnly);
	}

	return (
		<div>
			<div className={styles.sectionHeaderWrapper}>
				{award.isFetching && !award.isRefresh && (
					<div className={styles.sectionHeaderLoader}>
						<LoadingIndicator/>
					</div>
				)}
				<SectionHeader header={award.projectTitle} breadcrumbs={breadcrumbs}/>
			</div>

			<BasePadding>
				<VerticalSpacing>
					<AwardSummary
						parent={award.parent}
						expectedId={awardId}
						isFormComplete={isFormComplete}
					/>
				</VerticalSpacing>

				<Ref innerRef={tabsRef}>
					<Tabs
						className={styles.detailsTabs}
						panes={panes}
						disabled={isUploading}
					/>
				</Ref>
			</BasePadding>
		</div>
	);
};

export default AwardDetailView;
